// hooks/MessageContext.jsx
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { getUnreadMessagesCountByUser } from "./../services/messageServices";
import { useUser } from "./../contexts/UserContext";

const MessageContext = createContext({
  unreadMessageCount: 0,
  setUnreadMessageCount: () => {}, // compat rétro
  refresh: () => {},
});

export const MessageProvider = ({ children, pollMs = 60000 }) => {
  const { user } = useUser();
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const intervalRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user || !user.userId) return setUnreadMessageCount(0);
    try {
      const count = await getUnreadMessagesCountByUser();
      setUnreadMessageCount(Number(count || 0));
    } catch {}
  }, [user]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (user && user.userId) {
      refresh();
      intervalRef.current = setInterval(refresh, pollMs);

      const onVisibility = () => document.visibilityState === "visible" && refresh();
      const onFocus = () => refresh();
      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("focus", onFocus);

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("focus", onFocus);
      };
    } else {
      setUnreadMessageCount(0);
      return () => intervalRef.current && clearInterval(intervalRef.current);
    }
  }, [user, pollMs, refresh]);

  return (
    <MessageContext.Provider value={{ unreadMessageCount, setUnreadMessageCount, refresh }}>
      {children}
    </MessageContext.Provider>
  );
};

export const useMessage = () => useContext(MessageContext);

